import { Button, Col, Form, InputNumber, Modal, Row, Select, Switch } from "antd";
import useNotification from "utils/hooks/useNotification";
import useListCurrencies from "hooks/useListCurrencies";
import useCurrencyApi from "../hooks/useCurrencyApi";
import { ApolloQueryResult } from "@apollo/client";
import { getFormError } from "utils/getFormError";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import useCoreStore from "views/hooks/useCoreStore";
import { FaSave } from "react-icons/fa";

interface CreateCurrencyPropsTypes {
  refetch?: any;
  refetchSettings?:
    | ((
        variables?: Partial<GetVarTypeID> | undefined,
      ) => Promise<ApolloQueryResult<GetCompanySettingResponse>>)
    | undefined;
}

export default function CreateCurrency(props: CreateCurrencyPropsTypes): React.ReactElement {
  const { refetch, refetchSettings } = props;
  const [form] = Form.useForm();
  const urlParams: RouteParams = useParams();
  const code = Form.useWatch("code", form);

  const zeroDecimalCases = code === "XOF" || code === "XAF";

  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { listCurrencies, currenciesOptions } = useListCurrencies();
  const currency = useCoreStore(state => state.currency);

  const { createCurrency } = useCurrencyApi();
  const { notificationError, notificationSuccessfullySaved } = useNotification();

  const onFinish = async (values: Record<string, any>) => {
    let errors: any = [];

    const variables = {
      input: {
        companyId: urlParams.companyId,
        code: values.code?.toUpperCase(),
        decimalPlaces: values.decimalPlaces,
        isMainCurrency: values.isMainCurrency || false,
      },
    };

    const response = await createCurrency({ variables });

    errors = response?.data?.createCurrencySetting?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors, "Currency");
      form.setFields(fieldsData);
    } else {
      refetch();
      refetchSettings!();
      notificationSuccessfullySaved(t("Currency"));
      setIsModalVisible(false);
      form.resetFields();
    }
  };

  useEffect(() => {
    if (isModalVisible) {
      listCurrencies();
    }
  }, [isModalVisible]);

  useEffect(() => {
    if (zeroDecimalCases) {
      form.setFieldsValue({
        decimalPlaces: 0,
      });
    }
  }, [zeroDecimalCases]);

  useEffect(() => {
    if (isModalVisible && !currency) {
      form.setFieldsValue({
        isMainCurrency: true,
      });
    }
  }, [isModalVisible, currency]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form.submit();
  };

  return (
    <>
      <Row justify="end" className="margin-top">
        <Button type="primary" onClick={showModal}>
          {t("Add")}
        </Button>
      </Row>

      <Modal
        title={t("Add currency")}
        open={isModalVisible}
        onOk={handleOk}
        okText={t("Save")}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        onCancel={handleCancel}
        width={480}
        className="antd-modal"
      >
        <Form
          name="createCurrency"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={8}>
            <Col lg={12} sm={12} xs={24}>
              <Form.Item
                rules={[{ required: true, message: t("This field is required") }]}
                label={t("Code")}
                name="code"
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().startsWith(input.toLowerCase());
                  }}
                  filterSort={(optionA, optionB) => {
                    const valueA = String(optionA?.value ?? "");
                    const valueB = String(optionB?.value ?? "");

                    if (valueA === "EUR") return -1;
                    if (valueB === "EUR") return 1;

                    const labelA = String(optionA?.label ?? "");
                    const labelB = String(optionB?.label ?? "");
                    return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
                  }}
                  options={currenciesOptions}
                />
              </Form.Item>
            </Col>
            <Col lg={12} sm={12} xs={24}>
              <Form.Item
                rules={[{ required: true, message: t("This field is required") }]}
                label={t("Decimal cases")}
                name="decimalPlaces"
              >
                <InputNumber min={0} max={5} disabled={zeroDecimalCases} />
              </Form.Item>
            </Col>
            <Col lg={24} sm={24} xs={24}>
              <Form.Item label={t("Main currency")} name="isMainCurrency" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
